/* eslint-disable */
import type { DateTime } from '../../common/date_time';
import type { FareAmount } from '../../common/fare_amount';
import type { Money } from '../../common/money';
import { AirAncillariesResponseAncillaryAncillaryType } from '../air/air_ancillaries';
import type { ApprovalStatus } from '../common/approval';
import type { PreDefinedAnswers } from '../policy/user_defined_entity';
import type { PolicyInfoRuleResultInfo } from '../common/policy_info';

/**
 * Encapsulates the different types of statuses that would be shown to the end user. These could be
 * applicable for multiple entities like legs, bookings, trips etc.
 */
export enum UserFacingStatus {
  UNKNOWN_STATUS = 0,
  PENDING_STATUS = 1,
  CONFIRMED_STATUS = 2,
  ACTIVE_STATUS = 3,
  COMPLETED_STATUS = 4,
  CANCELLED_STATUS = 5,
  REFUNDED_STATUS = 6,
  VOIDED_STATUS = 7,
  /**
   * PROCESSING_STATUS - This status is used in cases where very little time has elapsed since the
   * user booked the ticket, and we don't have adequate information yet.
   */
  PROCESSING_STATUS = 8,
  /**
   * UNCONFIRMED_STATUS - This status is used in conjunction with `PROCESSING_STATUS`. If a long time
   * has elapsed since the user booked the ticket, and we don't have adequate
   * information yet, we use this status.
   */
  UNCONFIRMED_STATUS = 9,
  AIRLINE_CONTROL_STATUS = 10,
  PAYMENT_DECLINED_STATUS = 11,
  SCHEDULE_CHANGE_STATUS = 12,
  HOLD_STATUS = 13,
  /** APPROVAL_REQUESTED_STATUS - Approval statuses. */
  APPROVAL_REQUESTED_STATUS = 14,
  APPROVAL_DENIED_STATUS = 15,
  CANCELLATION_IN_PROGRESS_STATUS = 16,
  /** INOPERATIVE_STATUS - [Qantas only] Maps to `UN` flight status. */
  INOPERATIVE_STATUS = 17,
  /** FLIGHT_UNCONFIRMED_STATUS - [Qantas only] Maps to `UC` flight status. */
  FLIGHT_UNCONFIRMED_STATUS = 18,
  UNRECOGNIZED = -1,
}

export enum RateType {
  RATE_TYPE_UNKNOWN = 0,
  PUBLISHED = 1,
  TMC_NEGOTIATED = 2,
  COMPANY_NEGOTIATED = 3,
  UNRECOGNIZED = -1,
}

export interface PnrPolicyInfoPolicyId {
  id: string;
  version: string;
}

/** Baggage policy for a leg */
export interface AirBaggagePolicy {
  /** Policies for checked-in baggage */
  checkedIn: AirBaggagePolicyBaggageInfo[];
  /** Policies for carry-on baggage */
  carryOn: AirBaggagePolicyBaggageInfo[];
}

export interface AirBaggagePolicyBaggageInfo {
  description: string;
}

/** Exchange policy for a leg */
export interface AirExchangePolicy {
  description: string;
}

/** Cancellation policy for a leg */
export interface AirCancellationPolicy {
  description: string;
}

/** Other booking charges */
export interface OtherCharge {
  /** Total amount for extra item */
  amount?: Money;
  /** Name of extra item */
  type: string;
}

/** Lowest Logical Fare */
export interface LLFInfo {
  /** Lowest logical fare */
  llf?: Money;
  /** Reason code for selecting higher price than llf */
  reasonCode: PreDefinedAnswers;
  /** Free form text if selected reason code is OTHER */
  reason: string;
}

/** Message encapsulating all the approval related information for a PNR. */
export interface ApprovalInfo {
  /** The approval status of a PNR. */
  approvalStatus: ApprovalStatus;
}

/** Used by customers to sync their pnr id and versions with Spotnana */
export interface ExternalInfo {
  externalPnrId: string;
  externalPnrVersion: number;
}

/** Information related to the freshness of the PNR. */
export interface FreshnessInfo {
  /**
   * Time when the PNR information was updated in Spotnana's systems for the
   * latest PNR version available.
   */
  latestVersionTime?: DateTime;
  /**
   * Time when the PNR information was updated in Spotnana's systems for the
   * PNR whose information is in this object.
   */
  returnedVersionTime?: DateTime;
}

/** Information that will be used by the Booking Engine email system. */
export interface BookingEmailInfo {
  /**
   * Whether to disable sending an email. We use `disable_email` and not
   * `enable_email` because we want to default to sending an email.
   */
  disableEmail: boolean;
}

/** A field that will be used in PnrMetadata to set version specific information. */
export interface VersionSpecificMetadata {
  /** The PNR version this metadata is applicable for. */
  version: number;
  /**
   * The information that needs to be passed to the email engine that processes
   * PNR updates.
   */
  emailInfo?: BookingEmailInfo;
}

export interface OtherAncillary {
  /** Type of ancillary (Early bird, Wifi, etc.) */
  type: AirAncillariesResponseAncillaryAncillaryType;
  /**
   * Flight index to which this ancillary belongs. The index will be -1
   * for leg level or itinerary level ancillaries.
   *
   * @deprecated
   */
  flightIndex: number;
  /**
   * Leg index to which this ancillary belongs. The index will be -1
   * for itinerary level ancillaries.
   *
   * @deprecated
   */
  legIndex: number;
  /** Fare for this ancillary */
  fare?: FareAmount;
  status: OtherAncillaryAncillaryStatus;
  /** All the flights defined by <flight_index, leg_index> for which fare is applicable */
  flightIds: OtherAncillaryFlightId[];
}

/** Status for ancillary. */
export enum OtherAncillaryAncillaryStatus {
  /** ANCILLARY_STATUS_UNKNOWN - Ancillary status is unknown */
  ANCILLARY_STATUS_UNKNOWN = 0,
  /** CONFIRMED - Ancillary is confirmed */
  CONFIRMED = 1,
  /** PENDING - Ancillary is still pending confirmation from the airline */
  PENDING = 2,
  /** CANCELLED - Ancillary is denied or cancelled by the airline */
  CANCELLED = 3,
  /** ELIGIBLE - Ancillary is eligible for purchase */
  ELIGIBLE = 4,
  /** NOT_APPLICABLE - Ancillary is not applicable for flight */
  NOT_APPLICABLE = 5,
  UNRECOGNIZED = -1,
}

export interface OtherAncillaryFlightId {
  /** Index of flight within the leg for which the ancillary response. */
  flightIndex: number;
  /** Leg index for which the ancillary response. */
  legIndex: number;
}

/** Used by customers to sync their pnr id and versions with Spotnana */
export interface ExternalInfo {
  externalPnrId: string;
  externalPnrVersion: number;
  externalUrlMetadata: string;
}

export interface ApplicationInfo {
  applicationWarning: ApplicationInfoApplicationWarning[];
}

export interface ApplicationInfoApplicationWarning {
  /** Type of warning */
  warning: ApplicationInfoApplicationWarningWarningType;
  /** Description of warning */
  description: string;
}

export enum ApplicationInfoApplicationWarningWarningType {
  WARNING_TYPE_UNKNOWN = 0,
  LOYALTY_NAME_MISMATCH = 1,
  UNRECOGNIZED = -1,
}
export interface PnrPolicyInfo {
  /** The list of policies which were applied to the PNR. */
  policies: PnrPolicyInfoPolicyId[];
  /** List of rule results. */
  ruleResultInfos: PolicyInfoRuleResultInfo[];
}
